import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Layers, Linkedin } from "lucide-react";
import { articles } from "@/data/articles";
import { ArticleShareBar } from "@/components/articles/ArticleShareBar";
import { ReadingProgressBar } from "@/components/articles/ReadingProgressBar";
import { ArticleToc } from "@/components/articles/ArticleToc";
import { CiteThisArticle } from "@/components/articles/CiteThisArticle";

interface ArticleNavProps {
  currentSlug: string;
  hidePublicationDate?: boolean;
}

export const ArticleNav = ({ currentSlug, hidePublicationDate = false }: ArticleNavProps) => {
  const sorted = [...articles].sort(
    (a, b) => new Date(b.datePublished).getTime() - new Date(a.datePublished).getTime(),
  );
  const idx = sorted.findIndex((a) => a.slug === currentSlug);
  if (idx === -1) return null;

  const current = sorted[idx];
  const newer = idx > 0 ? sorted[idx - 1] : null;
  const older = idx < sorted.length - 1 ? sorted[idx + 1] : null;

  // Related: prefer same category; if fewer than 3, fill with most recent others.
  const sameCat = sorted.filter((a) => a.slug !== currentSlug && a.category === current.category);
  const others = sorted.filter((a) => a.slug !== currentSlug && a.category !== current.category);
  const related = [...sameCat, ...others].slice(0, 3);

  return (
    <>
      {/* Reading progress (fixed at top of viewport) */}
      <ReadingProgressBar />

      {/* Auto-generated ToC (sticky on desktop, collapsible on mobile) */}
      <ArticleToc />

      {/* Share bar */}
      <div className="mt-12 pt-8 border-t border-border/40">
        <p className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold mb-3">
          Share this essay
        </p>
        <ArticleShareBar path={current.path} title={current.title} description={current.description} />
      </div>

      {/* Cite */}
      <CiteThisArticle title={current.title} path={current.path} datePublished={hidePublicationDate ? undefined : current.datePublished} />

      {/* Author bio card */}
      <aside
        aria-label="About the author"
        className="mt-12 border-l-4 border-accent bg-card/60 p-6 rounded-sm"
      >
        <p className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold mb-3">
          About the Author
        </p>
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-14 w-14 rounded-full bg-accent/20 flex items-center justify-center text-accent font-display text-xl font-bold border border-accent/40">
            DI
          </div>
          <div>
            <p className="font-display text-base font-semibold text-foreground">David Isei</p>
            <p className="text-[12px] text-muted-foreground mb-2">
              Executive Director, Hawaiʻi Healthcare Task Force · U.S. Air Force veteran
            </p>
            <p className="text-[14px] text-foreground/90 leading-relaxed">
              David Isei is Executive Director of the Hawaiʻi Healthcare Task Force and builder
              of the Hawaiʻi Healthcare Intelligence Platform, which reconciles federal, state, and
              carrier data on Hawaiʻi's health system. He writes on workforce, insurer directories
              and network adequacy, and rural access, with every figure sourced and dated.
            </p>
            <a
              href="https://www.linkedin.com/in/david-isei/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-[12px] text-accent hover:text-accent/80"
            >
              <Linkedin className="h-3.5 w-3.5" /> LinkedIn
            </a>
          </div>
        </div>
      </aside>

      {/* Prev / Next */}
      <nav
        aria-label="Article navigation"
        className="mt-10 pt-8 border-t border-border/40 grid sm:grid-cols-2 gap-4"
      >
        {older ? (
          <Link
            to={older.path}
            className="group block rounded-lg border border-border/50 bg-card/40 p-5 hover:border-accent/60 hover:bg-card/70 transition-all"
          >
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-accent mb-2">
              <ArrowLeft className="h-3 w-3" /> Previous
            </div>
            <div className="font-display text-base font-semibold text-foreground group-hover:text-accent transition-colors leading-snug">
              {older.title}
            </div>
          </Link>
        ) : (
          <div />
        )}
        {newer ? (
          <Link
            to={newer.path}
            className="group block rounded-lg border border-border/50 bg-card/40 p-5 hover:border-accent/60 hover:bg-card/70 transition-all sm:text-right"
          >
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-accent mb-2 sm:justify-end w-full">
              Next <ArrowRight className="h-3 w-3" />
            </div>
            <div className="font-display text-base font-semibold text-foreground group-hover:text-accent transition-colors leading-snug">
              {newer.title}
            </div>
          </Link>
        ) : (
          <div />
        )}
      </nav>

      {/* In This Series */}
      <section
        aria-labelledby="series-heading"
        className="mt-12 pt-8 border-t border-border/40"
      >
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent mb-4">
          <Layers className="h-3.5 w-3.5" />
          <h2 id="series-heading" className="font-medium">In This Series</h2>
        </div>
        <ol className="space-y-1">
          {sorted.map((a, i) => {
            const isCurrent = a.slug === currentSlug;
            return (
              <li key={a.slug}>
                <Link
                  to={a.path}
                  aria-current={isCurrent ? "page" : undefined}
                  className={
                    isCurrent
                      ? "flex items-baseline gap-3 px-3 py-2 rounded-sm border-l-2 border-accent bg-accent/10 text-foreground font-semibold"
                      : "flex items-baseline gap-3 px-3 py-2 rounded-sm border-l-2 border-transparent text-muted-foreground hover:text-foreground hover:border-accent/40 transition-colors"
                  }
                >
                  <span className="text-[11px] font-mono tabular-nums text-muted-foreground w-6 shrink-0">
                    {String(sorted.length - i).padStart(2, "0")}
                  </span>
                  <span className="text-[14px] leading-snug">{a.title}</span>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      {related.length > 0 && (
        <section
          aria-labelledby="related-articles-heading"
          className="mt-12 pt-8 border-t border-border/40"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent mb-4">
            <BookOpen className="h-3.5 w-3.5" />
            <h2 id="related-articles-heading" className="font-medium">Related reading</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={a.path}
                className="group block rounded-lg border border-border/50 bg-card/40 p-5 hover:border-accent/60 hover:bg-card/70 transition-all"
              >
                <div className="font-display text-base font-semibold text-foreground group-hover:text-accent transition-colors leading-snug mb-2">
                  {a.title}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {a.description}
                </p>
                <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {a.readingMinutes} min read
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
};
